import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  CircularProgress,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Popover,
  Tooltip,
  Container,
  Tabs,
  Tab,
  ListSubheader,
  Alert,
  Table,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  GridLegacy as Grid,
  InputAdornment,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../components/PageHeader';
import api from '../api/api';
import { useFeedback } from '../context/FeedbackContext';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SchoolIcon from '@mui/icons-material/School';
import BookIcon from '@mui/icons-material/Book';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../context/AuthContext';
import { downloadStudentReport, downloadNominalRoll, bulkGenerateReports, /* bulkGenerateCombinedPDF, */ checkBulkReportStatus, getExamTrendAnalysis, generateTrendAnalysisPDF } from '../services/reportService';
import SignaturePad from '../components/SignaturePad';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import html2canvas from 'html2canvas';

function getDefaultDates() {
  return {
    start: '',
    end: '',
  };
}

const columns: GridColDef[] = [
  {
    field: 'date',
    headerName: 'Date',
    width: 120,
    valueFormatter: (params: any) => {
      // Support both DataGrid v5 (params.value) and v6+ (params is value)
      const value = typeof params === 'object' && params !== null && 'value' in params ? params.value : params;
      if (!value) return '-';
      const parts = String(value).split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
      return String(value);
    }
  },
  { field: 'day_of_week', headerName: 'Day', width: 110 },
  { field: 'student_name', headerName: 'Student', width: 180 },
  { field: 'student_id', headerName: 'Student ID', width: 100 },
  { field: 'class_level', headerName: 'Class Level', width: 130 },
  { field: 'school_id', headerName: 'School ID', width: 100 },
  { 
    field: 'status', 
    headerName: 'Status', 
    width: 120,
    renderCell: (params: any) => (
      <Chip
        label={params.value === 'present' ? 'Present' : 'Absent'}
        color={params.value === 'present' ? 'success' : 'error'}
        size="small"
        sx={{ minWidth: 70 }}
      />
    ),
  },
  { field: 'confidence', headerName: 'Confidence', width: 110, valueFormatter: (params: any) => (params && params.value != null ? Number(params.value).toFixed(2) : 'No data') },
  { field: 'review_status', headerName: 'Review Status', width: 130, valueFormatter: (params: any) => (params && params.value ? params.value : 'No review') },
];

// Add static class level enum list
const CLASS_LEVELS = [
  'Pre-Primary 1', 'Pre-Primary 2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6',
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
];

// Add type for Exam
interface Exam {
  id: number;
  title: string;
  [key: string]: any;
}

const Reports: React.FC = () => {
  const { showError, showSuccess } = useFeedback();
  const { user } = useAuth();
  // Chart refs
  const currentGradeChartRef = React.useRef<HTMLDivElement>(null);
  const currentSubjectChartRef = React.useRef<HTMLDivElement>(null);
  const prevGradeChartRef = React.useRef<HTMLDivElement>(null);
  const prevSubjectChartRef = React.useRef<HTMLDivElement>(null);
  const comparisonChartRef = React.useRef<HTMLDivElement>(null);

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.start);
  const [endDate, setEndDate] = useState<string>(defaultDates.end);
  const [courseId, setCourseId] = useState<number | ''>('');
  const [studentId, setStudentId] = useState<number | ''>('');
  const [status, setStatus] = useState<string>('');
  const [classLevel, setClassLevel] = useState<string>('');
  const [studentSearch, setStudentSearch] = useState('');
  const [minConfidence, setMinConfidence] = useState<string>('');
  const [reviewStatus, setReviewStatus] = useState<string>('');
  const [showQuickFilters, setShowQuickFilters] = useState<boolean>(false);
  const [showSavePreset, setShowSavePreset] = useState<boolean>(false);

  const [reportData, setReportData] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const [pdfExporting, setPdfExporting] = useState<boolean>(false);
  // Details dialog state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  // Help popover state
  const [helpAnchorEl, setHelpAnchorEl] = useState<null | HTMLElement>(null);
  const [tab, setTab] = useState(0);

  // Academic Reports tab state
  const [selectedClassLevel, setSelectedClassLevel] = useState<string>('');
  const [selectedExam, setSelectedExam] = useState<number | ''>('');
  const [selectedStudent, setSelectedStudent] = useState<number | ''>('');
  const [studentClassLevel, setStudentClassLevel] = useState<string>('');
  const [bulkStatus, setBulkStatus] = useState<string>('');
  const [bulkTaskId, setBulkTaskId] = useState<string>('');
  const [combinedPdfStatus, setCombinedPdfStatus] = useState<string>('');
  const [combinedPdfTaskId, setCombinedPdfTaskId] = useState<string>('');
  const [trendExam, setTrendExam] = useState<number | ''>('');
  const [trendPrevExam, setTrendPrevExam] = useState<number | ''>('');
  const [trendResult, setTrendResult] = useState<any>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendClassLevel, setTrendClassLevel] = useState<string>('');

  const [exams, setExams] = useState<Exam[]>([]);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [signatureSuccess, setSignatureSuccess] = useState(false);
  const [signatureError, setSignatureError] = useState<string | null>(null);

  const userId = user?.id;
  const userRole = user?.role;

  const fetchSignature = async () => {
    if (!userId || !userRole) return;
    setSignatureError(null);
    try {
      const res = await axios.get(`/api/signature/get-signature`, { 
        params: { user_id: userId, role: userRole }, 
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const url = URL.createObjectURL(res.data as Blob);
      setSignatureUrl(url);
    } catch (e: any) {
      setSignatureUrl(null);
      if (e?.response?.status !== 404) setSignatureError('Failed to fetch signature.');
    }
  };

  useEffect(() => {
    fetchSignature();
    // eslint-disable-next-line
  }, [userId, userRole]);

  const handleSignatureSave = async (dataUrl: string) => {
    setSignatureUploading(true);
    setSignatureSuccess(false);
    setSignatureError(null);
    try {
      const formData = new FormData();
      formData.append('user_id', String(userId || ''));
      formData.append('role', String(userRole || ''));
      formData.append('image_data', dataUrl);
      await axios.post('/api/signature/upload-signature', formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSignatureSuccess(true);
      fetchSignature();
    } catch (e) {
      setSignatureSuccess(false);
      setSignatureError('Failed to upload signature.');
    } finally {
      setSignatureUploading(false);
    }
  };

  useEffect(() => {
    if (!user || !user.school_id) {
      setCourses([]);
      setStudents([]);
      setDropdownLoading(false);
      return;
    }
    const fetchDropdownData = async () => {
      try {
        setDropdownLoading(true);
        const [coursesResponse, studentsResponse] = await Promise.all([
          api.get(`/courses/?school_id=${user.school_id}`),
          api.get(`/students/?school_id=${user.school_id}&limit=10000`),
        ]);
        setCourses(coursesResponse.data as any[]);
        setStudents(studentsResponse.data as any[]);
      } catch (error) {
        showError('Failed to load filter options. Please try refreshing the page.');
        setCourses([]);
        setStudents([]);
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchDropdownData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showError, user && user.school_id]);

  // Fetch exams for the current school
  useEffect(() => {
    const fetchExams = async () => {
      if (!user?.school_id) return;
      try {
        const res = await api.get(`/exams/?school_id=${user.school_id}`);
        setExams(res.data as Exam[]);
      } catch (e) {
        setExams([]);
      }
    };
    fetchExams();
  }, [user?.school_id]);

  const handleFetchReport = useCallback(async () => {
    if (!user || !user.school_id) {
      showError('User or school ID not found.');
      return;
    }
    setLoading(true);
    setReportData([]);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (courseId) params.append('course_id', String(courseId));
      if (studentId) params.append('student_id', String(studentId));
      if (status) params.append('status', status);
      if (classLevel) params.append('class_level', classLevel);
      params.append('school_id', String(user.school_id));
      const response = await api.get('/attendance/report', { params });
      const dataWithIds = (response.data as any[]).map((row: any, index: number) => ({
        ...row,
        id: `${row.student_id}-${row.date}-${index}`,
      }));
      setReportData(dataWithIds);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      let friendlyError = 'An unexpected error occurred while fetching the report.';
      if (typeof errorDetail === 'string') {
        friendlyError = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        friendlyError = errorDetail.map(e => e.msg || JSON.stringify(e)).join('; ');
      }
      showError(friendlyError);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, courseId, studentId, status, classLevel, showError, user]);

  const handleClearFilters = () => {
    setStartDate(defaultDates.start);
    setEndDate(defaultDates.end);
    setCourseId('');
    setStudentId('');
    setStatus('');
    setClassLevel('');
    setStudentSearch('');
    setMinConfidence('');
    setReviewStatus('');
  };

  const handleRemoveFilter = (filterKey: string) => {
    switch (filterKey) {
      case 'startDate':
        setStartDate(defaultDates.start);
        break;
      case 'endDate':
        setEndDate(defaultDates.end);
        break;
      case 'classLevel':
        setClassLevel('');
        break;
      case 'courseId':
        setCourseId('');
        break;
      case 'status':
        setStatus('');
        break;
      case 'studentId':
        setStudentId('');
        break;
      case 'minConfidence':
        setMinConfidence('');
        break;
      case 'reviewStatus':
        setReviewStatus('');
        break;
    }
  };

  const handleExportCSV = () => {
    setExporting(true);
    try {
      if (!reportData.length) {
        showError('No data to export.');
        setExporting(false);
        return;
      }
      const header = columns.map(col => col.headerName).join(',');
      const rows = reportData.map(row =>
        [row.date, row.day_of_week, row.student_name, row.student_id, row.class_level, row.school_id, row.status].join(',')
      );
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_report_${startDate}_to_${endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showSuccess('CSV exported!');
    } catch (e) {
      showError('Failed to export CSV.');
    } finally {
      setExporting(false);
    }
  };

  // PDF Export Handler
  const handleExportPDF = async () => {
    setPdfExporting(true);
    try {
      if (!reportData.length) {
        showError('No data to export.');
        setPdfExporting(false);
        return;
      }
      // Fetch school info
      let school = null;
      if (user?.school_id) {
        const res = await api.get(`/schools/${user.school_id}`);
        school = res.data;
      }
      console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
      console.log('school.logo:', (school as any)?.logo);
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const logoUrl = (school as any)?.logo.startsWith('/')
        ? `${backendUrl}${(school as any).logo}`
        : (school as any).logo;
      console.log('Logo URL:', logoUrl);
      const doc = new jsPDF();
      let y = 15;
      // Add logo if available
      if ((school as any)?.logo) {
        try {
          const img = await fetch(logoUrl)
            .then(r => r.blob())
            .then(blob => new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            }));
          doc.addImage(img, 'PNG', 15, y, 24, 24);
        } catch (e) {
          // Logo fetch failed, skip
        }
      }
      // Add school info
      doc.setFontSize(16);
      doc.text((school as any)?.name || 'School', 45, y + 8);
      doc.setFontSize(10);
      if ((school as any)?.address) doc.text(`Address: ${(school as any).address}`, 45, y + 15);
      if ((school as any)?.contact_info) doc.text(`Contact: ${(school as any).contact_info}`, 45, y + 21);
      y += 30;
      // Report title and filters
      doc.setFontSize(14);
      doc.text('Attendance Report', 15, y);
      y += 8;
      let filterText = '';
      if (startDate || endDate) filterText += `Date: ${startDate || '...'} to ${endDate || '...'}  `;
      if (classLevel) filterText += `Class: ${classLevel}  `;
      if (courseId) {
        const course = courses.find((c) => c.id === courseId);
        filterText += `Course: ${course ? course.title : courseId}  `;
      }
      if (studentId) {
        const student = students.find((s) => s.id === studentId);
        filterText += `Student: ${student ? student.name : studentId}  `;
      }
      if (status) filterText += `Status: ${status.charAt(0).toUpperCase() + status.slice(1)}`;
      if (filterText) {
        doc.setFontSize(10);
        doc.text(filterText, 15, y);
        y += 6;
      }
      // Table
      autoTable(doc, {
        startY: y,
        head: [columns.map(col => col.headerName ?? '')],
        body: reportData.map(row => [
          row.date ? new Date(row.date).toLocaleDateString() : '',
          row.day_of_week,
          row.student_name,
          row.student_id,
          row.class_level,
          row.school_id,
          row.status,
          row.confidence ? row.confidence.toFixed(2) : '',
          row.review_status || '',
        ]),
        styles: { fontSize: 9 },
      });
      doc.save(`attendance_report_${startDate}_to_${endDate}.pdf`);
      showSuccess('PDF exported!');
    } catch (e) {
      showError('Failed to export PDF.');
    } finally {
      setPdfExporting(false);
    }
  };

  // --- Details Dialog ---
  const handleRowClick = (params: any) => {
    setSelectedRow(params.row);
    setDetailsOpen(true);
  };
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedRow(null);
  };

  // --- Help Popover ---
  const handleHelpClick = (event: React.MouseEvent<HTMLElement>) => {
    setHelpAnchorEl(event.currentTarget);
  };
  const handleHelpClose = () => {
    setHelpAnchorEl(null);
  };
  const helpOpen = Boolean(helpAnchorEl);

  // --- Active Filter Chips ---
  const activeFilters = [];
  if (startDate || endDate) {
    activeFilters.push({ label: `Date: ${startDate || '...'} to ${endDate || '...'}`, key: 'date' });
  }
  if (classLevel) {
    activeFilters.push({ label: `Class: ${classLevel}`, key: 'classLevel' });
  }
  if (courseId) {
    const course = courses.find((c) => c.id === courseId);
    activeFilters.push({ label: `Course: ${course ? course.title : courseId}`, key: 'courseId' });
  }
  if (studentId) {
    const student = students.find((s) => s.id === studentId);
    activeFilters.push({ label: `Student: ${student ? student.name : studentId}`, key: 'studentId' });
  }
  if (status) {
    activeFilters.push({ label: `Status: ${status.charAt(0).toUpperCase() + status.slice(1)}`, key: 'status' });
  }
  if (minConfidence) {
    const confidencePercent = Math.round(parseFloat(minConfidence) * 100);
    activeFilters.push({ label: `Min Confidence: ${confidencePercent}%+`, key: 'minConfidence' });
  }
  if (reviewStatus) {
    activeFilters.push({ label: `Review: ${reviewStatus.charAt(0).toUpperCase() + reviewStatus.slice(1)}`, key: 'reviewStatus' });
  }

  // --- Sticky filter bar styles ---
  const stickyFilterBarSx = {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    background: 'rgba(245, 247, 250, 0.98)',
    borderBottom: '1px solid #e0e0e0',
    boxShadow: 1,
    mb: 4,
  };

  // Add debug log before DataGrid
  console.log('Attendance reportData:', reportData);

  // Filter exams and students by selected class level
  const filteredExams = selectedClassLevel ? exams.filter(e => e.class_level === selectedClassLevel) : [];
  const filteredStudents = selectedClassLevel ? students.filter(s => s.class_level === selectedClassLevel) : [];

  // Filter exams for trend analysis by class level
  const trendFilteredExams = trendClassLevel ? exams.filter(e => e.class_level === trendClassLevel) : exams;

  // --- PDF Export with Chart Images ---
  // Client-side PDF generation (existing functionality)
  async function handleExportPDFAnalysis(trendResult: any) {
    const doc = new jsPDF();
    let y = 10;
    // School details and timestamp
    const schoolName = user?.school_name || 'School';
    const schoolId = user?.school_id ? `School ID: ${user.school_id}` : '';
    const classLevel = trendResult.current_exam_summary?.class_level || '';
    const timestamp = new Date().toLocaleString();
    doc.setFontSize(16); doc.text(schoolName, 10, y); y += 8;
    if (schoolId) { doc.setFontSize(10); doc.text(schoolId, 10, y); y += 6; }
    if (classLevel) { doc.setFontSize(10); doc.text(`Class: ${classLevel}`, 10, y); y += 6; }
    doc.setFontSize(10); doc.text(`Generated on: ${timestamp}`, 10, y); y += 8;
    function addSectionTitle(title: string) {
      doc.setFontSize(14); doc.text(title, 10, y); y += 8;
    }
    function addKeyValueRows(obj: any) {
      if (!obj) return;
      Object.entries(obj).forEach(([k, v]) => {
        doc.setFontSize(10); doc.text(`${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? v.toFixed(2) : v}`, 12, y); y += 6;
      });
    }
    function addTable(title: string, data: any[], columns: string[]) {
      if (!data?.length) return;
      doc.setFontSize(12); doc.text(title, 10, y); y += 6;
      autoTable(doc, {
        startY: y,
        head: [columns],
        body: data.map(row => columns.map(col => row[col] ?? '')),
        theme: 'grid',
        styles: { fontSize: 9 },
        margin: { left: 10, right: 10 },
      });
      // In v5, we need to calculate the final Y position manually or use a callback
      y += (data.length + 1) * 10 + 6; // Approximate height based on rows
    }
    async function addChartImage(ref: React.RefObject<HTMLDivElement | null>, label: string, height = 60) {
      if (ref.current) {
        const canvas = await html2canvas(ref.current, { backgroundColor: null, scale: 2 });
        const imgData = canvas.toDataURL('image/png');
        doc.setFontSize(12); doc.text(label, 10, y); y += 4;
        doc.addImage(imgData, 'PNG', 10, y, 180, height);
        y += height + 6;
      }
    }
    // --- Current Exam ---
    if (trendResult.current_exam_summary) {
      addSectionTitle(`Current Exam: ${trendResult.current_exam_summary.exam_title}`);
      addKeyValueRows({
        Date: trendResult.current_exam_summary.exam_date?.slice(0, 10),
        Type: trendResult.current_exam_summary.exam_type,
        Class: trendResult.current_exam_summary.class_level,
        'Total Students': trendResult.current_exam_summary.total_students,
        'Total Marks': trendResult.current_exam_summary.total_marks,
        'Passing Marks': trendResult.current_exam_summary.passing_marks,
      });
      addSectionTitle('Overall Stats');
      addKeyValueRows(trendResult.current_exam_summary.overall_stats);
      addSectionTitle('Grade Distribution');
      addKeyValueRows(trendResult.current_exam_summary.grade_distribution);
      await addChartImage(currentGradeChartRef as React.RefObject<HTMLDivElement | null>, 'Grade Distribution');
      addTable('Top Performers', trendResult.current_exam_summary.insights?.top_performers, ['student_name', 'score', 'percentage']);
      addTable('Needs Attention', trendResult.current_exam_summary.insights?.needs_attention, ['student_name', 'score', 'percentage']);
      addSectionTitle('Subject Analysis');
      await addChartImage(currentSubjectChartRef as React.RefObject<HTMLDivElement | null>, 'Subject Analysis');
      addTable('Subject Analysis', trendResult.current_exam_summary.subject_analysis, ['course_title', 'average_score', 'highest_score', 'lowest_score', 'passing_rate']);
    }
    // --- Previous Exam ---
    if (trendResult.previous_exam_summary) {
      addSectionTitle(`Previous Exam: ${trendResult.previous_exam_summary.exam_title}`);
      addKeyValueRows({
        Date: trendResult.previous_exam_summary.exam_date?.slice(0, 10),
        Type: trendResult.previous_exam_summary.exam_type,
        Class: trendResult.previous_exam_summary.class_level,
        'Total Students': trendResult.previous_exam_summary.total_students,
        'Total Marks': trendResult.previous_exam_summary.total_marks,
        'Passing Marks': trendResult.previous_exam_summary.passing_marks,
      });
      addSectionTitle('Overall Stats');
      addKeyValueRows(trendResult.previous_exam_summary.overall_stats);
      addSectionTitle('Grade Distribution');
      addKeyValueRows(trendResult.previous_exam_summary.grade_distribution);
      await addChartImage(prevGradeChartRef as React.RefObject<HTMLDivElement | null>, 'Grade Distribution');
      addTable('Top Performers', trendResult.previous_exam_summary.insights?.top_performers, ['student_name', 'score', 'percentage']);
      addTable('Needs Attention', trendResult.previous_exam_summary.insights?.needs_attention, ['student_name', 'score', 'percentage']);
      addSectionTitle('Subject Analysis');
      await addChartImage(prevSubjectChartRef as React.RefObject<HTMLDivElement | null>, 'Subject Analysis');
      addTable('Subject Analysis', trendResult.previous_exam_summary.subject_analysis, ['course_title', 'average_score', 'highest_score', 'lowest_score', 'passing_rate']);
    }
    // --- Comparison ---
    if (trendResult.comparison) {
      addSectionTitle('Comparison: Overall Stats Difference');
      addKeyValueRows(trendResult.comparison.overall_stats_diff);
      addSectionTitle('Subject Performance Diff');
      await addChartImage(comparisonChartRef as React.RefObject<HTMLDivElement | null>, 'Subject Performance Diff');
      addTable('Subject Performance Diff', trendResult.comparison.subject_performance_diff, ['course_title', 'average_score_diff', 'passing_rate_diff']);
      addTable('Student Performance Diff', trendResult.comparison.student_performance_diff, ['student_name', 'score_diff', 'percentage_diff', 'rank_diff']);
    }
    doc.save('exam_trend_analysis.pdf');
  }

  // Backend PDF generation (new functionality)
  const [backendPdfLoading, setBackendPdfLoading] = useState(false);
  
  async function handleBackendExportPDF() {
    if (!trendExam || !trendPrevExam) {
      alert('Please select both current and previous exams');
      return;
    }
    
    setBackendPdfLoading(true);
    try {
      await generateTrendAnalysisPDF(trendExam as number, trendPrevExam as number);
    } catch (error: any) {
      console.error('Backend PDF generation error:', error);
      alert(`Failed to generate PDF: ${error.message}`);
    } finally {
      setBackendPdfLoading(false);
    }
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: '#fafbfc',
      py: 3
    }}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Enhanced Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3
          }}>
            <Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 700, 
                color: '#2c3e50',
                mb: 1
              }}>
                Reports Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                Comprehensive reporting and analytics for your school management system
              </Typography>
            </Box>
            
                         {/* Quick Stats Cards */}
             <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
               <Card sx={{ 
                 minWidth: 120, 
                 background: 'white',
                 color: '#2c3e50',
                 boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                 border: '1px solid #e1e8ed',
                 '&:hover': {
                   boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                   transform: 'translateY(-2px)',
                   transition: 'all 0.2s ease'
                 }
               }}>
                 <CardContent sx={{ p: 2, textAlign: 'center' }}>
                   <Typography variant="h4" sx={{ fontWeight: 700, color: '#3498db' }}>
                     {reportData.length}
                   </Typography>
                   <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                     Records
                   </Typography>
                 </CardContent>
               </Card>
               
               <Card sx={{ 
                 minWidth: 120, 
                 background: 'white',
                 color: '#2c3e50',
                 boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                 border: '1px solid #e1e8ed',
                 '&:hover': {
                   boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                   transform: 'translateY(-2px)',
                   transition: 'all 0.2s ease'
                 }
               }}>
                 <CardContent sx={{ p: 2, textAlign: 'center' }}>
                   <Typography variant="h4" sx={{ fontWeight: 700, color: '#e74c3c' }}>
                     {students.length}
                   </Typography>
                   <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                     Students
                   </Typography>
                 </CardContent>
               </Card>
               
               <Card sx={{ 
                 minWidth: 120, 
                 background: 'white',
                 color: '#2c3e50',
                 boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                 border: '1px solid #e1e8ed',
                 '&:hover': {
                   boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                   transform: 'translateY(-2px)',
                   transition: 'all 0.2s ease'
                 }
               }}>
                 <CardContent sx={{ p: 2, textAlign: 'center' }}>
                   <Typography variant="h4" sx={{ fontWeight: 700, color: '#27ae60' }}>
                     {courses.length}
                   </Typography>
                   <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                     Courses
                   </Typography>
                 </CardContent>
               </Card>
             </Box>
          </Box>
        </Box>

        {/* Enhanced Tab Navigation */}
        <Paper sx={{ 
          borderRadius: 2, 
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          mb: 4,
          border: '1px solid #e1e8ed'
        }}>
          <Box sx={{ 
            background: 'white',
            p: 1
          }}>
            <Tabs 
              value={tab} 
              onChange={(_, v) => setTab(v)} 
              aria-label="report tabs"
              sx={{
                '& .MuiTab-root': {
                  color: '#7f8c8d',
                  fontWeight: 500,
                  fontSize: '1rem',
                  textTransform: 'none',
                  minHeight: 56,
                  '&.Mui-selected': {
                    color: '#3498db',
                    background: '#f8f9fa',
                    borderRadius: 2
                  },
                  '&:hover': {
                    color: '#3498db',
                    background: '#f8f9fa'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#3498db',
                  height: 3
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterListIcon />
                    Attendance Reports
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon />
                    Academic Reports
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon />
                    Analytics
                  </Box>
                } 
              />
            </Tabs>
          </Box>
        </Paper>
      {tab === 0 && (
        <Box>
          {/* Enhanced Attendance Reports Header */}
          <Paper sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            background: 'white',
            color: '#2c3e50',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e1e8ed'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#2c3e50' }}>
                  📊 Attendance Reports
                </Typography>
                <Typography variant="body1" sx={{ color: '#7f8c8d' }}>
                  Comprehensive attendance tracking and analysis with advanced filtering capabilities
                </Typography>
              </Box>
              
                             {/* Enhanced Export Buttons */}
               <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                 <Button
                   onClick={handleExportCSV}
                   disabled={loading || exporting || !reportData.length}
                   variant="contained"
                   sx={{ 
                     background: '#3498db',
                     color: 'white',
                     '&:hover': {
                       background: '#2980b9'
                     }
                   }}
                   startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                 >
                   {exporting ? 'Exporting...' : 'Export CSV'}
                 </Button>
                 
                 <Button
                   onClick={handleExportPDF}
                   disabled={loading || pdfExporting || !reportData.length}
                   variant="contained"
                   sx={{ 
                     background: '#e74c3c',
                     color: 'white',
                     '&:hover': {
                       background: '#c0392b'
                     }
                   }}
                   startIcon={pdfExporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                 >
                   {pdfExporting ? 'Exporting...' : 'Export PDF'}
                 </Button>
                 
                 <Tooltip title="How to use the reports page">
                   <IconButton 
                     aria-label="help" 
                     onClick={handleHelpClick}
                     sx={{ 
                       color: '#7f8c8d',
                       border: '1px solid #e1e8ed',
                       '&:hover': {
                         background: '#f8f9fa',
                         color: '#3498db'
                       }
                     }}
                   >
                     <HelpOutlineIcon />
                   </IconButton>
                 </Tooltip>
               </Box>
            </Box>
          </Paper>
          
          <Popover
            open={helpOpen}
            anchorEl={helpAnchorEl}
            onClose={handleHelpClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, maxWidth: 320 }}>
              <Typography variant="subtitle1" gutterBottom>How to use this page</Typography>
              <Typography variant="body2" gutterBottom>
                Use the filters to narrow down attendance records by date, course, subject, or status. Click "Run Report" to view results. You can export the data as CSV, and click a row for more details.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tip: The filter bar stays visible as you scroll for easy access.
              </Typography>
            </Box>
          </Popover>
          
          <Paper elevation={3} sx={{ 
            ...stickyFilterBarSx, 
            p: 3, 
            borderRadius: 2,
            background: 'white',
            border: '1px solid #e1e8ed',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            {/* Filter Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon sx={{ color: '#3498db' }} />
                <Typography variant="h6" sx={{ color: '#2c3e50' }}>Advanced Report Filters</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Quick Filters">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<TuneIcon />}
                    onClick={() => setShowQuickFilters(!showQuickFilters)}
                  >
                    Quick Filters
                  </Button>
                </Tooltip>
                <Tooltip title="Clear All Filters">
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    disabled={loading}
                  >
                    Clear All
                  </Button>
                </Tooltip>
              </Box>
            </Box>

                {/* Quick Filters */}
    {showQuickFilters && (
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e1e8ed' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ color: '#2c3e50' }}>Quick Filter Presets</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="Today"
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setStartDate(today);
                      setEndDate(today);
                    }}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="This Week"
                    onClick={() => {
                      const today = new Date();
                      const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
                      const endOfWeek = new Date(today.setDate(today.getDate() - today.getDay() + 6));
                      setStartDate(startOfWeek.toISOString().split('T')[0]);
                      setEndDate(endOfWeek.toISOString().split('T')[0]);
                    }}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="This Month"
                    onClick={() => {
                      const today = new Date();
                      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                      setStartDate(startOfMonth.toISOString().split('T')[0]);
                      setEndDate(endOfMonth.toISOString().split('T')[0]);
                    }}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="Present Only"
                    onClick={() => setStatus('present')}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="Absent Only"
                    onClick={() => setStatus('absent')}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label="High Confidence"
                    onClick={() => setMinConfidence('0.9')}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            )}

            {/* Main Filter Grid */}
            <Grid container spacing={2}>
              {/* Date Range */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Class Level</InputLabel>
                  <Select 
                    value={classLevel} 
                    label="Class Level" 
                    onChange={(e) => setClassLevel(e.target.value as string)}
                    startAdornment={
                      <InputAdornment position="start">
                        <SchoolIcon fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value=""><em>All Levels</em></MenuItem>
                    {CLASS_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>{level}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" disabled={dropdownLoading}>
                  <InputLabel>Course</InputLabel>
                  <Select 
                    value={courseId} 
                    label="Course" 
                    onChange={(e) => setCourseId(e.target.value as number | '')}
                    startAdornment={
                      <InputAdornment position="start">
                        <BookIcon fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value=""><em>All Courses</em></MenuItem>
                    {courses.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Status and Student */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select 
                    value={status} 
                    label="Status" 
                    onChange={(e) => setStatus(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <CheckCircleIcon fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value=""><em>All Status</em></MenuItem>
                    <MenuItem value="present">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        Present
                      </Box>
                    </MenuItem>
                    <MenuItem value="absent">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CancelIcon color="error" fontSize="small" />
                        Absent
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" disabled={dropdownLoading}>
                  <InputLabel>Student</InputLabel>
                  <Select
                    value={studentId}
                    label="Student"
                    onChange={(e) => setStudentId(e.target.value as number | '')}
                    renderValue={(selected) => {
                      const student = students.find((s) => s.id === selected);
                      return student ? student.name : 'All Students';
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                    startAdornment={
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value=""><em>All Students</em></MenuItem>
                    <ListSubheader>
                      <TextField
                        size="small"
                        placeholder="Search student..."
                        value={studentSearch}
                        onChange={e => setStudentSearch(e.target.value)}
                        fullWidth
                        autoFocus
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ListSubheader>
                    {students.filter((s) =>
                      !studentSearch || s.name.toLowerCase().includes(studentSearch.toLowerCase())
                    ).map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            {s.name.charAt(0)}
                          </Avatar>
                          {s.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Confidence Range */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Min Confidence</InputLabel>
                  <Select 
                    value={minConfidence} 
                    label="Min Confidence" 
                    onChange={(e) => setMinConfidence(e.target.value as string)}
                    startAdornment={
                      <InputAdornment position="start">
                        <TrendingUpIcon fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value=""><em>Any</em></MenuItem>
                    <MenuItem value="0.9">90%+ (High)</MenuItem>
                    <MenuItem value="0.8">80%+ (Good)</MenuItem>
                    <MenuItem value="0.7">70%+ (Fair)</MenuItem>
                    <MenuItem value="0.6">60%+ (Low)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Review Status */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Review Status</InputLabel>
                  <Select 
                    value={reviewStatus} 
                    label="Review Status" 
                    onChange={(e) => setReviewStatus(e.target.value as string)}
                    startAdornment={
                      <InputAdornment position="start">
                        <RateReviewIcon fontSize="small" color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value=""><em>All</em></MenuItem>
                    <MenuItem value="pending">Pending Review</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Tooltip title="Run Report with Current Filters">
                <Button
                  onClick={handleFetchReport}
                  disabled={loading}
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  size="medium"
                  sx={{ minWidth: 140 }}
                >
                  {loading ? 'Loading...' : 'Run Report'}
                </Button>
              </Tooltip>
              
              <Tooltip title="Save Filter Preset">
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  size="medium"
                  onClick={() => setShowSavePreset(true)}
                >
                  Save Preset
                </Button>
              </Tooltip>

              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Tooltip title="Export Current Results">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    size="medium"
                    onClick={handleExportCSV}
                    disabled={reportData.length === 0}
                  >
                    Export CSV
                  </Button>
                </Tooltip>
                
                <Tooltip title="Export as PDF">
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    size="medium"
                    onClick={handleExportPDF}
                    disabled={reportData.length === 0}
                  >
                    Export PDF
                  </Button>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Active Filters Display */}
            {activeFilters.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FilterListIcon fontSize="small" />
                  Active filters:
                </Typography>
                {activeFilters.map((filter) => (
                  <Chip 
                    key={filter.key} 
                    label={filter.label} 
                    color="primary" 
                    size="small" 
                    variant="outlined"
                    onDelete={() => handleRemoveFilter(filter.key)}
                    deleteIcon={<CloseIcon />}
                  />
                ))}
              </Box>
            )}
          </Paper>
          
          {/* Enhanced Data Grid Container */}
          <Paper sx={{ 
            height: 600, 
            width: '100%', 
            position: 'relative', 
            p: 3, 
            background: 'white',
            border: '1px solid #e1e8ed',
            borderRadius: 2, 
            mt: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            {/* Enhanced Loading Overlay */}
            {loading && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(5px)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 3,
                borderRadius: 3
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  p: 4,
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.8)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}>
                  <CircularProgress size={48} sx={{ color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" color="primary.main" gutterBottom>
                    Loading Report Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please wait while we fetch your attendance records...
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Enhanced Data Grid */}
            <DataGrid
              rows={reportData}
              columns={columns}
              loading={loading}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              getRowId={(row) => row.id}
              sx={{
                opacity: loading ? 0.5 : 1,
                '& .MuiDataGrid-virtualScroller': { minHeight: 400 },
                cursor: 'pointer',
                border: 'none',
                '& .MuiDataGrid-row': {
                  borderRadius: 1,
                  margin: '2px 0',
                  '&:hover': {
                    background: '#f8f9fa',
                    color: '#2c3e50',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease-in-out'
                  },
                },
                '& .MuiDataGrid-columnHeaders': {
                  background: '#3498db',
                  color: 'white',
                  borderRadius: 2,
                  '& .MuiDataGrid-columnHeader': {
                    border: 'none',
                    '&:hover': {
                      background: '#2980b9'
                    }
                  }
                },
                '& .MuiDataGrid-cell': {
                  border: 'none',
                  padding: '12px 16px'
                },
                '& .MuiDataGrid-footerContainer': {
                  background: '#f8f9fa',
                  borderRadius: 2
                }
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 8, 
                    color: 'grey.600',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '50%',
                      background: '#f8f9fa',
                      mb: 2,
                      border: '1px solid #e1e8ed'
                    }}>
                      <RefreshIcon sx={{ fontSize: 48, opacity: 0.5, color: '#3498db' }} />
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ color: '#2c3e50' }}>
                      No Report Data Found
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                      Try adjusting your filters and running the report again. You can also try different date ranges or class levels.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleFetchReport}
                      sx={{ mt: 2 }}
                    >
                      Run Report
                    </Button>
                  </Box>
                ),
              }}
              onRowClick={handleRowClick}
            />
          </Paper>
          
          {/* Enhanced Details Dialog */}
          <Dialog 
            open={detailsOpen} 
            onClose={handleCloseDetails} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                background: 'white',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid #e1e8ed'
              }
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              background: '#3498db',
              color: 'white',
              borderRadius: '8px 8px 0 0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SearchIcon sx={{ color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Attendance Record Details
                </Typography>
              </Box>
              <IconButton 
                aria-label="close" 
                onClick={handleCloseDetails} 
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {selectedRow ? (
                <Grid container spacing={3}>
                  {/* Student Information */}
                  <Grid item xs={12} md={6}>
                                         <Card sx={{ 
                       p: 2, 
                       background: '#f8f9fa',
                       borderRadius: 2,
                       border: '1px solid #e1e8ed'
                     }}>
                       <Typography variant="h6" gutterBottom sx={{ color: '#2c3e50', fontWeight: 600 }}>
                         👤 Student Information
                       </Typography>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                         <Avatar sx={{ 
                           width: 48, 
                           height: 48, 
                           background: '#3498db',
                           fontSize: '1.2rem'
                         }}>
                           {selectedRow.student_name?.charAt(0)}
                         </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {selectedRow.student_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {selectedRow.student_id}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Class Level:</strong> {selectedRow.class_level}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>School ID:</strong> {selectedRow.school_id}
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Attendance Details */}
                  <Grid item xs={12} md={6}>
                                         <Card sx={{ 
                       p: 2, 
                       background: '#f8f9fa',
                       borderRadius: 2,
                       border: '1px solid #e1e8ed'
                     }}>
                       <Typography variant="h6" gutterBottom sx={{ color: '#2c3e50', fontWeight: 600 }}>
                         📅 Attendance Details
                       </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: '50%',
                          background: 'rgba(255,255,255,0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CalendarTodayIcon color="primary" />
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {selectedRow.date ? new Date(selectedRow.date).toLocaleDateString() : '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedRow.day_of_week}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Chip 
                          label={selectedRow.status === 'present' ? 'Present' : 'Absent'} 
                          color={selectedRow.status === 'present' ? 'success' : 'error'} 
                          size="medium"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '1rem',
                            padding: '8px 16px'
                          }}
                        />
                      </Box>
                    </Card>
                  </Grid>

                  {/* Technical Details */}
                  <Grid item xs={12}>
                                         <Card sx={{ 
                       p: 2, 
                       background: '#f8f9fa',
                       borderRadius: 2,
                       border: '1px solid #e1e8ed'
                     }}>
                       <Typography variant="h6" gutterBottom sx={{ color: '#2c3e50', fontWeight: 600 }}>
                         🔧 Technical Details
                       </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TrendingUpIcon color="action" fontSize="small" />
                            <Typography variant="body2">
                              <strong>Confidence:</strong> {selectedRow.confidence != null ? `${(selectedRow.confidence * 100).toFixed(1)}%` : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <RateReviewIcon color="action" fontSize="small" />
                            <Typography variant="body2">
                              <strong>Review Status:</strong> {selectedRow.review_status || 'Not Reviewed'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No details available
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
                             <Button 
                 onClick={handleCloseDetails} 
                 variant="contained"
                 sx={{
                   background: '#3498db',
                   color: 'white',
                   borderRadius: 2,
                   px: 3,
                   '&:hover': {
                     background: '#2980b9'
                   }
                 }}
               >
                 Close
               </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {tab === 1 && (
        <Box>
          <PageHeader title="Academic Reports" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Generate and download academic reports such as report cards, nominal rolls, and trend analysis.
          </Typography>
          {/* Student Report Card Panel */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Student Report Card (PDF)</Typography>
            {exams.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No exams available. Please create an exam first.</Alert>}
            {CLASS_LEVELS.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No class levels available.</Alert>}
            {selectedClassLevel && filteredStudents.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No students found for the selected class level.</Alert>}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={CLASS_LEVELS.length === 0}>
                <InputLabel>Class Level</InputLabel>
                <Select value={selectedClassLevel} label="Class Level" onChange={e => {
                  setSelectedClassLevel(e.target.value as string);
                  setSelectedExam('');
                  setSelectedStudent('');
                }}>
                  <MenuItem value=""><em>Select Class Level</em></MenuItem>
                  {CLASS_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={!selectedClassLevel || filteredExams.length === 0}>
                <InputLabel>Exam</InputLabel>
                <Select value={selectedExam} label="Exam" onChange={e => setSelectedExam(e.target.value as number | '')}>
                  <MenuItem value=""><em>Select Exam</em></MenuItem>
                  {filteredExams.map((exam: Exam) => (
                    <MenuItem key={exam.id} value={exam.id}>{exam.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={!selectedClassLevel || filteredStudents.length === 0}>
                <InputLabel>Student</InputLabel>
                <Select value={selectedStudent} label="Student" onChange={e => setSelectedStudent(e.target.value as number | '')}>
                  <MenuItem value=""><em>Select Student</em></MenuItem>
                  {filteredStudents.map((student) => (
                    <MenuItem key={student.id} value={student.id}>{student.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                disabled={!selectedClassLevel || !selectedExam || !selectedStudent}
                onClick={() => downloadStudentReport(selectedExam as number, selectedStudent as number)}
              >
                Download Report Card
              </Button>
            </Box>
            {/* SignaturePad Integration */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Teacher Signature</Typography>
              {signatureUrl && (
                <Box sx={{ mb: 1 }}>
                  <Typography color="text.secondary">Current Signature:</Typography>
                  <img src={signatureUrl} alt="Signature Preview" style={{ border: '1px solid #ccc', marginTop: 4, maxWidth: 200 }} />
                </Box>
              )}
              <SignaturePad onSave={handleSignatureSave} />
              {signatureUploading && <Typography color="info.main">Uploading signature...</Typography>}
              {signatureSuccess && <Typography color="success.main">Signature uploaded successfully!</Typography>}
              {signatureError && <Typography color="error.main">{signatureError}</Typography>}
            </Box>
          </Paper>
          {/* Nominal Roll Panel */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Nominal Roll (PDF)</Typography>
            {exams.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No exams available. Please create an exam first.</Alert>}
            {CLASS_LEVELS.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No class levels available.</Alert>}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={CLASS_LEVELS.length === 0}>
                <InputLabel>Class Level</InputLabel>
                <Select value={selectedClassLevel} label="Class Level" onChange={e => {
                  setSelectedClassLevel(e.target.value as string);
                  setSelectedExam('');
                }}>
                  <MenuItem value=""><em>Select Class Level</em></MenuItem>
                  {CLASS_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={!selectedClassLevel || filteredExams.length === 0}>
                <InputLabel>Exam</InputLabel>
                <Select value={selectedExam} label="Exam" onChange={e => setSelectedExam(e.target.value as number | '')}>
                  <MenuItem value=""><em>Select Exam</em></MenuItem>
                  {filteredExams.map((exam: Exam) => (
                    <MenuItem key={exam.id} value={exam.id}>{exam.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="secondary"
                disabled={!selectedClassLevel || !selectedExam}
                onClick={() => downloadNominalRoll(selectedExam as number, selectedClassLevel)}
              >
                Download Nominal Roll
              </Button>
            </Box>
          </Paper>
          {/* Bulk Generate Reports Panel */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Bulk Generate Student Reports (ZIP)</Typography>
            {exams.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No exams available. Please create an exam first.</Alert>}
            {CLASS_LEVELS.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No class levels available.</Alert>}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={CLASS_LEVELS.length === 0}>
                <InputLabel>Class Level</InputLabel>
                <Select value={selectedClassLevel} label="Class Level" onChange={e => {
                  setSelectedClassLevel(e.target.value as string);
                  setSelectedExam('');
                }}>
                  <MenuItem value=""><em>Select Class Level</em></MenuItem>
                  {CLASS_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={!selectedClassLevel || filteredExams.length === 0}>
                <InputLabel>Exam</InputLabel>
                <Select value={selectedExam} label="Exam" onChange={e => setSelectedExam(e.target.value as number | '')}>
                  <MenuItem value=""><em>Select Exam</em></MenuItem>
                  {filteredExams.map((exam: Exam) => (
                    <MenuItem key={exam.id} value={exam.id}>{exam.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="info"
                disabled={!selectedClassLevel || !selectedExam}
                onClick={async () => {
                  setBulkStatus('Starting...');
                  try {
                    const res = await bulkGenerateReports(selectedExam as number, selectedClassLevel);
                    setBulkTaskId(res.task_id);
                    setBulkStatus('Processing...');
                    // Poll for status
                    const poll = setInterval(async () => {
                      const statusRes = await checkBulkReportStatus(res.task_id);
                      if (statusRes.status === 'completed') {
                        setBulkStatus('Completed!');
                        clearInterval(poll);
                        if (statusRes.result && typeof statusRes.result === 'string') {
                          // Construct full URL using backend base URL
                          const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                          const fullUrl = statusRes.result.startsWith('http') 
                            ? statusRes.result 
                            : `${backendUrl}${statusRes.result}`;
                          window.open(fullUrl, '_blank');
                        }
                      } else if (statusRes.status === 'failed') {
                        setBulkStatus('Failed: ' + statusRes.result);
                        clearInterval(poll);
                      } else {
                        setBulkStatus('Processing...');
                      }
                    }, 2000);
                  } catch (e: any) {
                    setBulkStatus('Failed to start: ' + (e?.message || e));
                  }
                }}
              >
                Bulk Generate & Download ZIP
              </Button>
              {/* Combined PDF button disabled temporarily
              <Button
                variant="contained"
                color="secondary"
                disabled={!selectedClassLevel || !selectedExam}
                onClick={async () => {
                  setCombinedPdfStatus('Starting...');
                  try {
                    const res = await bulkGenerateCombinedPDF(selectedExam as number, selectedClassLevel);
                    setCombinedPdfTaskId(res.task_id);
                    setCombinedPdfStatus('Processing...');
                    // Poll for status
                    const poll = setInterval(async () => {
                      const statusRes = await checkBulkReportStatus(res.task_id);
                      if (statusRes.status === 'completed') {
                        setCombinedPdfStatus('Completed!');
                        clearInterval(poll);
                        if (statusRes.result && typeof statusRes.result === 'string') {
                          // Construct full URL using backend base URL
                          const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                          const fullUrl = statusRes.result.startsWith('http') 
                            ? statusRes.result 
                            : `${backendUrl}${statusRes.result}`;
                          window.open(fullUrl, '_blank');
                        }
                      } else if (statusRes.status === 'failed') {
                        setCombinedPdfStatus('Failed: ' + statusRes.result);
                        clearInterval(poll);
                      } else {
                        setCombinedPdfStatus('Processing...');
                      }
                    }, 2000);
                  } catch (e: any) {
                    setCombinedPdfStatus('Failed to start: ' + (e?.message || e));
                  }
                }}
              >
                Generate Combined PDF
              </Button>
              */}
              {bulkStatus && <Typography sx={{ ml: 2 }}>{bulkStatus}</Typography>}
              {combinedPdfStatus && <Typography sx={{ ml: 2 }}>{combinedPdfStatus}</Typography>}
            </Box>
          </Paper>
          {/* Trend Analysis Panel */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Exam Trend Analysis</Typography>
            {exams.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No exams available. Please create an exam first.</Alert>}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Class Level</InputLabel>
                <Select value={trendClassLevel} label="Class Level" onChange={e => {
                  setTrendClassLevel(e.target.value as string);
                  setTrendExam('');
                  setTrendPrevExam('');
                }}>
                  <MenuItem value=""><em>Select Class Level</em></MenuItem>
                  {CLASS_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={trendFilteredExams.length === 0}>
                <InputLabel>Current Exam</InputLabel>
                <Select value={trendExam} label="Current Exam" onChange={e => setTrendExam(e.target.value as number | '')}>
                  <MenuItem value=""><em>Select Exam</em></MenuItem>
                  {trendFilteredExams.map((exam: Exam) => (
                    <MenuItem key={exam.id} value={exam.id}>{exam.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} size="small" disabled={trendFilteredExams.length === 0}>
                <InputLabel>Previous Exam</InputLabel>
                <Select value={trendPrevExam} label="Previous Exam" onChange={e => setTrendPrevExam(e.target.value as number | '')}>
                  <MenuItem value=""><em>Select Exam</em></MenuItem>
                  {trendFilteredExams.map((exam: Exam) => (
                    <MenuItem key={exam.id} value={exam.id}>{exam.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="success"
                  disabled={!trendClassLevel || !trendExam || !trendPrevExam}
                  onClick={async () => {
                    setTrendLoading(true);
                    setTrendResult(null);
                    try {
                      const res = await getExamTrendAnalysis(trendExam as number, trendPrevExam as number);
                      setTrendResult(res);
                    } catch (e: any) {
                      setTrendResult({ error: e?.message || e });
                    } finally {
                      setTrendLoading(false);
                    }
                  }}
                >
                  Generate Trend Analysis
                </Button>
                <Tooltip title="Generate PDF report directly from server (no preview required)">
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={!trendExam || !trendPrevExam || backendPdfLoading}
                    onClick={handleBackendExportPDF}
                    startIcon={backendPdfLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                  >
                    {backendPdfLoading ? 'Generating PDF...' : 'Generate PDF Report'}
                  </Button>
                </Tooltip>
              </Box>
            </Box>
            {trendLoading && <Typography>Loading trend analysis...</Typography>}
            {trendResult && (
              <Box sx={{ mt: 2 }}>
                {trendResult.error ? (
                  <Typography color="error">{trendResult.error}</Typography>
                ) : (
                  <Box>
                    {trendResult && !trendResult.error && (
                      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Tooltip title="Generate PDF using client-side libraries (includes charts)">
                          <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={() => handleExportPDFAnalysis(trendResult)} 
                            startIcon={<PictureAsPdfIcon />}
                          >
                            Export Analysis as PDF (Client)
                          </Button>
                        </Tooltip>
                        <Tooltip title="Generate PDF using server-side processing (more reliable)">
                          <Button 
                            variant="contained" 
                            color="secondary" 
                            onClick={handleBackendExportPDF}
                            disabled={backendPdfLoading}
                            startIcon={backendPdfLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                          >
                            {backendPdfLoading ? 'Generating...' : 'Export Analysis as PDF (Backend)'}
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                    {/* --- Current Exam Summary --- */}
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Current Exam: {trendResult.current_exam_summary?.exam_title}</Typography></AccordionSummary>
                      <AccordionDetails>
                        {trendResult.current_exam_summary && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Date: {trendResult.current_exam_summary.exam_date?.slice(0, 10)} | Type: {trendResult.current_exam_summary.exam_type} | Class: {trendResult.current_exam_summary.class_level}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                              <Chip label={`Total Students: ${trendResult.current_exam_summary.total_students}`} />
                              <Chip label={`Total Marks: ${trendResult.current_exam_summary.total_marks}`} />
                              <Chip label={`Passing Marks: ${trendResult.current_exam_summary.passing_marks}`} />
                            </Stack>
                            {/* Overall Stats */}
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">Overall Stats</Typography>
                              <Stack direction="row" spacing={2} flexWrap="wrap">
                                {Object.entries(trendResult.current_exam_summary.overall_stats || {}).map(([k, v]) => (
                                  <Chip key={k} label={`${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? v.toFixed(2) : v}`}/>
                                ))}
                              </Stack>
                            </Box>
                            {/* Grade Distribution Bar Chart */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Grade Distribution</Typography>
                              <ResponsiveContainer width="100%" height={220} ref={currentGradeChartRef}>
                                <BarChart data={Object.entries(trendResult.current_exam_summary.grade_distribution || {}).map(([grade, count]) => ({ grade, count }))}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="grade" />
                                  <YAxis allowDecimals={false} />
                                  <RechartsTooltip />
                                  <Bar dataKey="count" fill="#1976d2">
                                    <LabelList dataKey="count" position="top" />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            {/* Export Top Performers */}
                            <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mb: 1 }} onClick={() => exportTableToCSV(trendResult.current_exam_summary.insights?.top_performers, 'top_performers_current.csv')}>Export Top Performers CSV</Button>
                            {/* Top Performers */}
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">Top Performers</Typography>
                              <Table size="small"><thead><tr><th>Name</th><th>Score</th><th>%</th></tr></thead><tbody>
                                {(trendResult.current_exam_summary.insights?.top_performers || []).map((s: any) => (
                                  <tr key={s.student_id}><td>{s.student_name}</td><td>{s.score}</td><td>{s.percentage?.toFixed(1)}</td></tr>
                                ))}
                              </tbody></Table>
                            </Box>
                            {/* Export Needs Attention */}
                            <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mb: 1 }} onClick={() => exportTableToCSV(trendResult.current_exam_summary.insights?.needs_attention, 'needs_attention_current.csv')}>Export Needs Attention CSV</Button>
                            {/* Needs Attention */}
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">Needs Attention</Typography>
                              <Table size="small"><thead><tr><th>Name</th><th>Score</th><th>%</th></tr></thead><tbody>
                                {(trendResult.current_exam_summary.insights?.needs_attention || []).map((s: any) => (
                                  <tr key={s.student_id}><td>{s.student_name}</td><td>{s.score}</td><td>{s.percentage?.toFixed(1)}</td></tr>
                                ))}
                              </tbody></Table>
                            </Box>
                            {/* Subject Analysis Bar Chart */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Subject Analysis</Typography>
                              <ResponsiveContainer width="100%" height={260} ref={currentSubjectChartRef}>
                                <BarChart data={(trendResult.current_exam_summary.subject_analysis || []).map((subj: any) => ({ subject: subj.course_title, avg: subj.average_score }))}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="subject" />
                                  <YAxis />
                                  <RechartsTooltip />
                                  <Bar dataKey="avg" fill="#43a047">
                                    <LabelList dataKey="avg" position="top" />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                              <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mt: 1 }} onClick={() => exportTableToCSV(trendResult.current_exam_summary.subject_analysis, 'subject_analysis_current.csv')}>Export Subject Analysis CSV</Button>
                            </Box>
                            {/* Subject Analysis Table */}
                            <Table size="small"><thead><tr><th>Subject</th><th>Avg</th><th>High</th><th>Low</th><th>Passing Rate</th></tr></thead><tbody>
                              {(trendResult.current_exam_summary.subject_analysis || []).map((subj: any) => (
                                <tr key={subj.course_id}>
                                  <td>{subj.course_title}</td>
                                  <td>{subj.average_score?.toFixed(1)}</td>
                                  <td>{subj.highest_score}</td>
                                  <td>{subj.lowest_score}</td>
                                  <td>{subj.passing_rate?.toFixed(1)}%</td>
                                </tr>
                              ))}
                            </tbody></Table>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                    {/* --- Previous Exam Summary --- */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Previous Exam: {trendResult.previous_exam_summary?.exam_title}</Typography></AccordionSummary>
                      <AccordionDetails>
                        {trendResult.previous_exam_summary && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Date: {trendResult.previous_exam_summary.exam_date?.slice(0, 10)} | Type: {trendResult.previous_exam_summary.exam_type} | Class: {trendResult.previous_exam_summary.class_level}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                              <Chip label={`Total Students: ${trendResult.previous_exam_summary.total_students}`} />
                              <Chip label={`Total Marks: ${trendResult.previous_exam_summary.total_marks}`} />
                              <Chip label={`Passing Marks: ${trendResult.previous_exam_summary.passing_marks}`} />
                            </Stack>
                            {/* Overall Stats */}
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">Overall Stats</Typography>
                              <Stack direction="row" spacing={2} flexWrap="wrap">
                                {Object.entries(trendResult.previous_exam_summary.overall_stats || {}).map(([k, v]) => (
                                  <Chip key={k} label={`${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? v.toFixed(2) : v}`}/>
                                ))}
                              </Stack>
                            </Box>
                            {/* Grade Distribution Bar Chart */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Grade Distribution</Typography>
                              <ResponsiveContainer width="100%" height={220} ref={prevGradeChartRef}>
                                <BarChart data={Object.entries(trendResult.previous_exam_summary.grade_distribution || {}).map(([grade, count]) => ({ grade, count }))}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="grade" />
                                  <YAxis allowDecimals={false} />
                                  <RechartsTooltip />
                                  <Bar dataKey="count" fill="#1976d2">
                                    <LabelList dataKey="count" position="top" />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            {/* Export Top Performers */}
                            <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mb: 1 }} onClick={() => exportTableToCSV(trendResult.previous_exam_summary.insights?.top_performers, 'top_performers_previous.csv')}>Export Top Performers CSV</Button>
                            {/* Top Performers */}
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">Top Performers</Typography>
                              <Table size="small"><thead><tr><th>Name</th><th>Score</th><th>%</th></tr></thead><tbody>
                                {(trendResult.previous_exam_summary.insights?.top_performers || []).map((s: any) => (
                                  <tr key={s.student_id}><td>{s.student_name}</td><td>{s.score}</td><td>{s.percentage?.toFixed(1)}</td></tr>
                                ))}
                              </tbody></Table>
                            </Box>
                            {/* Export Needs Attention */}
                            <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mb: 1 }} onClick={() => exportTableToCSV(trendResult.previous_exam_summary.insights?.needs_attention, 'needs_attention_previous.csv')}>Export Needs Attention CSV</Button>
                            {/* Needs Attention */}
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2">Needs Attention</Typography>
                              <Table size="small"><thead><tr><th>Name</th><th>Score</th><th>%</th></tr></thead><tbody>
                                {(trendResult.previous_exam_summary.insights?.needs_attention || []).map((s: any) => (
                                  <tr key={s.student_id}><td>{s.student_name}</td><td>{s.score}</td><td>{s.percentage?.toFixed(1)}</td></tr>
                                ))}
                              </tbody></Table>
                            </Box>
                            {/* Subject Analysis Bar Chart */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Subject Analysis</Typography>
                              <ResponsiveContainer width="100%" height={260} ref={prevSubjectChartRef}>
                                <BarChart data={(trendResult.previous_exam_summary.subject_analysis || []).map((subj: any) => ({ subject: subj.course_title, avg: subj.average_score }))}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="subject" />
                                  <YAxis />
                                  <RechartsTooltip />
                                  <Bar dataKey="avg" fill="#43a047">
                                    <LabelList dataKey="avg" position="top" />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                              <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mt: 1 }} onClick={() => exportTableToCSV(trendResult.previous_exam_summary.subject_analysis, 'subject_analysis_previous.csv')}>Export Subject Analysis CSV</Button>
                            </Box>
                            {/* Subject Analysis Table */}
                            <Table size="small"><thead><tr><th>Subject</th><th>Avg</th><th>High</th><th>Low</th><th>Passing Rate</th></tr></thead><tbody>
                              {(trendResult.previous_exam_summary.subject_analysis || []).map((subj: any) => (
                                <tr key={subj.course_id}>
                                  <td>{subj.course_title}</td>
                                  <td>{subj.average_score?.toFixed(1)}</td>
                                  <td>{subj.highest_score}</td>
                                  <td>{subj.lowest_score}</td>
                                  <td>{subj.passing_rate?.toFixed(1)}%</td>
                                </tr>
                              ))}
                            </tbody></Table>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                    {/* --- Comparison Section --- */}
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Comparison</Typography></AccordionSummary>
                      <AccordionDetails>
                        {trendResult.comparison && (
                          <Box>
                            {/* Overall Stats Diff */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Overall Stats Difference</Typography>
                              <Stack direction="row" spacing={2} flexWrap="wrap">
                                {Object.entries(trendResult.comparison.overall_stats_diff || {}).map(([k, v]) => (
                                  <Chip key={k} label={`${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? (v > 0 ? '+' : '') + v.toFixed(2) : v}`}
                                    color={typeof v === 'number' ? (v > 0 ? 'success' : v < 0 ? 'error' : 'default') : 'default'} />
                                ))}
                              </Stack>
                            </Box>
                            {/* Subject Performance Diff Grouped Bar Chart */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2">Subject Performance Difference</Typography>
                              <ResponsiveContainer width="100%" height={260} ref={comparisonChartRef}>
                                <BarChart data={(trendResult.comparison.subject_performance_diff || []).map((subj: any) => ({ subject: subj.course_title, avg_diff: subj.average_score_diff, pass_diff: subj.passing_rate_diff }))}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="subject" />
                                  <YAxis />
                                  <RechartsTooltip />
                                  <Legend />
                                  <Bar dataKey="avg_diff" fill="#1976d2" name="Avg Diff">
                                    <LabelList dataKey="avg_diff" position="top" />
                                  </Bar>
                                  <Bar dataKey="pass_diff" fill="#43a047" name="Passing Rate Diff">
                                    <LabelList dataKey="pass_diff" position="top" />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                              <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mt: 1 }} onClick={() => exportTableToCSV(trendResult.comparison.subject_performance_diff, 'subject_performance_diff.csv')}>Export Subject Performance Diff CSV</Button>
                            </Box>
                            {/* Subject Performance Diff Table */}
                            <Table size="small"><thead><tr><th>Subject</th><th>Avg Diff</th><th>Passing Rate Diff</th></tr></thead><tbody>
                              {(trendResult.comparison.subject_performance_diff || []).map((subj: any) => (
                                <tr key={subj.course_id}>
                                  <td>{subj.course_title}</td>
                                  <td style={{ color: subj.average_score_diff > 0 ? 'green' : subj.average_score_diff < 0 ? 'red' : undefined }}>
                                    {subj.average_score_diff > 0 ? '+' : ''}{subj.average_score_diff?.toFixed(2)}
                                  </td>
                                  <td style={{ color: subj.passing_rate_diff > 0 ? 'green' : subj.passing_rate_diff < 0 ? 'red' : undefined }}>
                                    {subj.passing_rate_diff > 0 ? '+' : ''}{subj.passing_rate_diff?.toFixed(2)}%
                                  </td>
                                </tr>
                              ))}
                            </tbody></Table>
                            {/* Student Performance Diff Export */}
                            <Button size="small" startIcon={<FileDownloadIcon />} sx={{ mt: 1 }} onClick={() => exportTableToCSV(trendResult.comparison.student_performance_diff, 'student_performance_diff.csv')}>Export Student Performance Diff CSV</Button>
                            {/* Student Performance Diff Table */}
                            <Table size="small"><thead><tr><th>Name</th><th>Score Diff</th><th>% Diff</th><th>Rank Diff</th></tr></thead><tbody>
                              {(trendResult.comparison.student_performance_diff || []).map((s: any) => (
                                <tr key={s.student_id}>
                                  <td>{s.student_name}</td>
                                  <td style={{ color: s.score_diff > 0 ? 'green' : s.score_diff < 0 ? 'red' : undefined }}>
                                    {s.score_diff > 0 ? '+' : ''}{s.score_diff}
                                  </td>
                                  <td style={{ color: s.percentage_diff > 0 ? 'green' : s.percentage_diff < 0 ? 'red' : undefined }}>
                                    {s.percentage_diff > 0 ? '+' : ''}{s.percentage_diff?.toFixed(2)}
                                  </td>
                                  <td style={{ color: s.rank_diff < 0 ? 'green' : s.rank_diff > 0 ? 'red' : undefined }}>
                                    {s.rank_diff > 0 ? '+' : ''}{s.rank_diff}
                                  </td>
                                </tr>
                              ))}
                            </tbody></Table>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}
      {tab === 2 && (
        <Box>
          <PageHeader title="Other Reports" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            More report types and analytics will be available soon.
          </Typography>
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="body1">Coming soon...</Typography>
          </Paper>
        </Box>
      )}
        </Container>
      </Box>
    );
};

// --- CSV Export Helper ---
function exportTableToCSV(data: any[], filename: string) {
  if (!data || !data.length) return;
  const keys = Object.keys(data[0]);
  const csvRows = [keys.join(','), ...data.map(row => keys.map(k => JSON.stringify(row[k] ?? '')).join(','))];
  const csv = csvRows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default Reports; 